import { useCallback, useMemo, useRef, useState } from "react";
import { api } from "common";

const normalizeContact = (value) => String(value || "").trim().toLowerCase();
const normalizePhone = (value) => normalizeContact(value).replace(/[^0-9+]/g, "");

const isExactLookupCandidate = (value) => {
  if (value.includes("@")) {
    return true;
  }

  if (/^[+0-9\s()-]{7,}$/.test(value)) {
    return true;
  }

  return value.length >= 20 && !/\s/.test(value);
};

const hasLocalContactMatch = (users, value) => {
  const normalizedValue = normalizeContact(value);
  const normalizedPhone = normalizePhone(value);

  return users.some((user) => {
    const ids = [user.id, user.uid, user.email].map(normalizeContact);
    return ids.includes(normalizedValue) || normalizePhone(user.mobile) === normalizedPhone;
  });
};

export default function useLegacyUserSearch({ enabled, expectedUserType, users, t }) {
  const { lookupUserByContact } = api;
  const requestId = useRef(0);
  const [matchedUser, setMatchedUser] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState(null);

  const onSearchChange = useCallback(async (rawValue) => {
    const value = String(rawValue || "").trim();
    const currentRequestId = ++requestId.current;

    setMatchedUser(null);
    setEmptyMessage(null);

    if (!enabled || !isExactLookupCandidate(value) || hasLocalContactMatch(users, value)) {
      return;
    }

    const result = await lookupUserByContact({ contact: value });
    if (currentRequestId !== requestId.current) {
      return;
    }

    if (result?.status === "profile-exists" && result.profile) {
      const profileUserType = result.profile.usertype || result.profile.userType;
      if (profileUserType === expectedUserType) {
        setMatchedUser(result.profile);
      } else {
        setEmptyMessage(t("legacy_user_wrong_table", {
          defaultValue: "User found under a different user type.",
        }));
      }
      return;
    }

    if (result?.status === "auth-only") {
      setEmptyMessage(t("legacy_user_auth_only_help", {
        defaultValue: "This Auth account has no app profile. Signup with the same phone/email will repair it.",
      }));
      return;
    }

    if (result?.status === "contact-conflict") {
      setEmptyMessage(t("legacy_user_status_contact-conflict", {
        defaultValue: "Contact conflict",
      }));
      return;
    }

    if (result?.error) {
      setEmptyMessage(result.error);
    }
  }, [enabled, expectedUserType, lookupUserByContact, t, users]);

  const tableData = useMemo(() => {
    if (!matchedUser || users.some((user) => user.id === matchedUser.id)) {
      return users;
    }

    return [matchedUser, ...users];
  }, [matchedUser, users]);

  return {
    emptyMessage,
    onSearchChange,
    tableData,
  };
}
